import dayjs from "dayjs";
import { useId } from "react";
import { useIntl } from "react-intl";

import { Alert } from "@/components/Alert/Alert";
import { Divider } from "@/components/Divider/Divider";
import { Image } from "@/components/Image/Image";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import type { TripDetails } from "@/features/trips/types/types";

type Props = {
  trip: TripDetails;
  startDate: string | undefined;
};

export function OverviewCard({ trip, startDate }: Props) {
  const intl = useIntl();
  const alertTitleId = useId();

  const startDateFormatted = dayjs(startDate).format("D MMMM, YYYY");
  const endDateFormatted = dayjs(startDate)
    .add(trip.durationInDays, "day")
    .format("D MMMM, YYYY");

  return (
    <div className="rounded-3xl bg-white p-1">
      <Image
        src={trip.imageUrls[0]}
        alt={trip.name}
        className="aspect-7/2 w-full rounded-t-2xl object-cover"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.name}
        </div>
        <div className="text-sm text-text-tertiary">{trip.subtitle}</div>

        <div className="my-3">
          <Divider color="neutral-soft" />
        </div>

        <DetailsList>
          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel start date",
              description: "Label for the trip start / departure date",
            })}
            value={
              startDate ? (
                startDateFormatted
              ) : (
                <Skeleton>
                  <span>{startDateFormatted}</span>
                </Skeleton>
              )
            }
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel end date",
              description: "Label for the trip end / return date",
            })}
            value={
              startDate ? (
                endDateFormatted
              ) : (
                <Skeleton>
                  <span>{endDateFormatted}</span>
                </Skeleton>
              )
            }
          />
        </DetailsList>

        <div className="mt-4">
          <Alert severity="info" aria-labelledby={alertTitleId}>
            <div
              className="mb-1 text-sm font-medium text-black"
              id={alertTitleId}
            >
              {intl.formatMessage({
                defaultMessage:
                  "Full trip details will be shared after confirmation",
                description: "Heading of the booking information notice card",
              })}
            </div>
            <div className="text-sm text-text-placeholder">
              {intl.formatMessage({
                defaultMessage:
                  "Our team will contact you with the flight & hotel details, and other trip information or requirements after confirmation",
                description:
                  "Body text in the booking information notice card on the trip booking page",
              })}
            </div>
          </Alert>
        </div>
      </div>
    </div>
  );
}
